import { useEffect, useState, type FormEvent } from "react";
import Modal from "@/components/base/Modal";
import { Field, Input } from "@/components/base/FormControls";
import { cn } from "@/utils/cn";
import {
  ACTION_LABELS,
  DEFAULT_ROLE_TEMPLATES,
  PERMISSION_ACTIONS,
  PERMISSION_MODULES,
  countPermissions,
  type PermissionAction,
  type PermissionMap,
} from "@/store/permissions/catalog";
import type { RoleEmploye } from "@/store/permissions/rolesData";
import { useRoles } from "@/store/permissions/RolesProvider";

interface RoleFormModalProps {
  open: boolean;
  onClose: () => void;
  role?: RoleEmploye | null;
  onSaved?: () => void;
}

export default function RoleFormModal({ open, onClose, role, onSaved }: RoleFormModalProps) {
  const { createRole, updateRole } = useRoles();
  const [nom, setNom] = useState("");
  const [permissions, setPermissions] = useState<PermissionMap>({});
  const [error, setError] = useState<string | null>(null);
  const [errorNom, setErrorNom] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setNom(role ? role.nom : "");
    setPermissions(role ? { ...role.permissions } : {});
    setError(null);
    setErrorNom(null);
    setSaving(false);
  }, [open, role]);

  const toggle = (module: string, action: PermissionAction) =>
    setPermissions((prev) => ({
      ...prev,
      [module]: { ...(prev[module] ?? {}), [action]: !prev[module]?.[action] },
    }));

  const isModuleOn = (module: string) =>
    PERMISSION_MODULES.find((m) => m.key === module)?.actions.every((action) => permissions[module]?.[action]) ?? false;

  const applyTemplate = (templateName: string, templatePermissions: PermissionMap) => {
    setNom(templateName);
    setPermissions(JSON.parse(JSON.stringify(templatePermissions)) as PermissionMap);
  };

  const enableAll = () => {
    const next: PermissionMap = {};
    PERMISSION_MODULES.forEach((module) => {
      next[module.key] = module.actions.reduce(
        (acc, action) => ({ ...acc, [action]: true }),
        {} as Partial<Record<PermissionAction, boolean>>
      );
    });
    setPermissions(next);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!nom.trim()) {
      setErrorNom("Le nom du rôle est obligatoire.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (role) await updateRole(role.id, nom, permissions);
      else await createRole(nom, permissions);
      onSaved?.();
      onClose();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Enregistrement impossible.");
    } finally {
      setSaving(false);
    }
  };

  const total = countPermissions(permissions);

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="xl"
      title={role ? "Modifier le rôle" : "Nouveau rôle"}
      description="Définissez le nom du rôle puis cochez les permissions par module. Les employés de ce rôle héritent de ces droits (réductibles individuellement)."
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center justify-center whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-5 py-2.5 text-sm font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer"
          >
            Annuler
          </button>
          <button
            type="submit"
            form="role-form"
            disabled={saving}
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 cursor-pointer"
          >
            <i className={cn(saving ? "ri-loader-4-line animate-spin" : "ri-check-line", "text-base")} aria-hidden="true" />
            {saving ? "Enregistrement..." : role ? "Enregistrer" : "Créer le rôle"}
          </button>
        </>
      }
    >
      <form id="role-form" onSubmit={handleSubmit} className="flex flex-col gap-5">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end">
          <Field label="Nom du rôle" htmlFor="role-nom" required error={errorNom ?? undefined} className="flex-1">
            <Input
              id="role-nom"
              value={nom}
              onChange={(e) => setNom(e.target.value)}
              placeholder="Ex. Gestionnaire, Caissier, Comptable..."
              maxLength={60}
              icon="ri-shield-user-line"
            />
          </Field>
          <span className="inline-flex h-[42px] shrink-0 items-center gap-1.5 rounded-md border border-secondary-200 bg-secondary-50 px-3.5 text-xs font-label font-medium text-secondary-900">
            <i className="ri-checkbox-multiple-line text-sm" aria-hidden="true" />
            {total} permission{total > 1 ? "s" : ""}
          </span>
        </div>

        {!role && (
          <div className="flex flex-col gap-2 rounded-md border border-background-200 bg-background-100/60 p-3">
            <p className="text-xs font-label font-medium text-foreground-700">Partir d'un modèle (optionnel)</p>
            <div className="flex flex-wrap gap-1.5">
              {DEFAULT_ROLE_TEMPLATES.map((template) => (
                <button
                  key={template.code}
                  type="button"
                  onClick={() => applyTemplate(template.nom, template.permissions)}
                  className="inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-2.5 py-1 text-[11px] font-label font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:bg-primary-50 hover:text-primary-900 cursor-pointer"
                >
                  {template.nom}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="rounded-lg border border-background-200 bg-background-50">
          <div className="flex items-center gap-2.5 border-b border-background-200 px-4 py-3">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-secondary-50 text-secondary-600">
              <i className="ri-shield-keyhole-line text-base" aria-hidden="true" />
            </span>
            <div>
              <h3 className="font-heading text-sm font-semibold text-foreground-950">Matrice des permissions</h3>
              <p className="text-xs text-foreground-500">Cochez les actions autorisées pour chaque module.</p>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[620px] border-collapse text-sm">
              <thead>
                <tr className="bg-background-100/70">
                  <th className="sticky left-0 z-10 bg-background-100/95 px-4 py-2.5 text-left text-xs font-label font-semibold uppercase tracking-wide text-foreground-600">
                    Module
                  </th>
                  {PERMISSION_ACTIONS.map((action) => (
                    <th
                      key={action}
                      className="px-3 py-2.5 text-center text-xs font-label font-semibold uppercase tracking-wide text-foreground-600"
                    >
                      {ACTION_LABELS[action]}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-background-200">
                {PERMISSION_MODULES.map((module) => (
                  <tr key={module.key} className="transition-colors hover:bg-background-100/40">
                    <td className="sticky left-0 z-10 bg-background-50 px-4 py-2.5">
                      <div className="flex min-w-0 items-center gap-2.5">
                        <span
                          className={cn(
                            "flex h-7 w-7 shrink-0 items-center justify-center rounded-md",
                            isModuleOn(module.key) ? "bg-primary-100 text-primary-700" : "bg-background-100 text-foreground-500"
                          )}
                        >
                          <i className={cn(module.icon, "text-sm")} aria-hidden="true" />
                        </span>
                        <div className="min-w-0">
                          <p className="truncate font-label font-medium text-foreground-900">{module.label}</p>
                          <p className="text-[11px] text-foreground-400">{module.group}</p>
                        </div>
                      </div>
                    </td>
                    {PERMISSION_ACTIONS.map((action) => {
                      const supported = module.actions.includes(action);
                      if (!supported) {
                        return (
                          <td key={action} className="px-3 py-2.5 text-center text-foreground-300">
                            —
                          </td>
                        );
                      }
                      const active = Boolean(permissions[module.key]?.[action]);
                      return (
                        <td key={action} className="px-3 py-2.5 text-center">
                          <button
                            type="button"
                            onClick={() => toggle(module.key, action)}
                            aria-pressed={active}
                            aria-label={`${module.label} - ${ACTION_LABELS[action]}`}
                            className={cn(
                              "inline-flex h-8 w-8 items-center justify-center rounded-md border transition-colors cursor-pointer",
                              active
                                ? "border-primary-300 bg-primary-100 text-primary-800"
                                : "border-background-300 bg-background-50 text-foreground-300 hover:bg-background-100"
                            )}
                          >
                            <i
                              className={cn(active ? "ri-checkbox-circle-fill" : "ri-checkbox-blank-circle-line", "text-base")}
                              aria-hidden="true"
                            />
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-background-200 px-4 py-2.5">
            <button
              type="button"
              onClick={() => setPermissions({})}
              className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-label font-medium text-foreground-600 transition-colors hover:text-foreground-950 cursor-pointer"
            >
              <i className="ri-eraser-line text-sm" aria-hidden="true" />
              Tout décocher
            </button>
            <button
              type="button"
              onClick={enableAll}
              className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-label font-medium text-foreground-600 transition-colors hover:text-foreground-950 cursor-pointer"
            >
              <i className="ri-check-double-line text-sm" aria-hidden="true" />
              Tout activer
            </button>
          </div>
        </div>

        {error && (
          <p className="flex items-start gap-2 rounded-md border border-primary-200 bg-primary-50/60 px-3.5 py-2.5 text-xs text-primary-800">
            <i className="ri-error-warning-line mt-0.5 text-sm" aria-hidden="true" />
            {error}
          </p>
        )}
      </form>
    </Modal>
  );
}